import dayjs from "dayjs";
import { useTranslation } from "react-i18next";
import CommonStyles from "@components/CommonStyles";
import { IPatientRecord } from "@base/modules/user/profile/profile.interface";
import { ILifeInsurance } from "@base/modules/patientInsurance/lifeInsurance.interface";
import { getCardTemplate } from "./cardTemplates";
import InsuranceCard from "./InsuranceCard";

interface IProps {
  insurance: ILifeInsurance;
  patientRecord: IPatientRecord;
  onViewDetail: () => void;
  /** Edit/delete controls, when the list allows them. */
  actions?: React.ReactNode;
}

/** One health insurance: its member card when it has one, otherwise the plan and its dates. */
export default function LifeInsuranceItem({ insurance, patientRecord, onViewDetail, actions }: IProps) {
  //! state
  const { t } = useTranslation();
  const template = getCardTemplate(insurance);

  //! function
  const formatDate = (value?: string) => (value ? dayjs(value).format("DD/MM/YYYY") : "");

  //! render
  return (
    <div className="bg-common-background rounded-xl p-4 flex flex-col gap-3">
      <div className="flex justify-between items-center gap-3">
        <div className="text-common-maintext text-base font-semibold font-['Inter'] min-w-0 break-words">{insurance.health_insurance?.provider}</div>

        <div className="flex items-center gap-2 shrink-0">
          <CommonStyles.ButtonUI type="primary" ghost onClick={onViewDetail}>
            {t("user.profile.viewDetail")}
          </CommonStyles.ButtonUI>
          {actions}
        </div>
      </div>

      {template ? (
        <InsuranceCard insurance={insurance} template={template} patientRecord={patientRecord} />
      ) : (
        <div className="flex flex-col gap-1 text-sm font-['Inter']">
          <div>
            <span className="text-common-subtext">{t("user.profile.insurance.insuranceType")}: </span>
            <span className="text-common-maintext">{insurance.health_insurance?.name ?? insurance.name}</span>
          </div>
          <div>
            <span className="text-common-subtext">{t("user.profile.insurance.effectiveDate")}: </span>
            <span className="text-common-maintext">{formatDate(insurance.start_date)}</span>
          </div>
          <div>
            <span className="text-common-subtext">{t("user.profile.insurance.expirationDate")}: </span>
            <span className="text-common-maintext">{formatDate(insurance.end_date)}</span>
          </div>
        </div>
      )}
    </div>
  );
}
